/**
 * P3 · React error boundary — captures + reports frontend crashes to Sentry
 * (when the DSN is configured) and shows a friendly retry card.
 *
 * Kept minimal: no external UI dep beyond the app's existing shadcn Button
 * and Card, so import cost is near-zero when Sentry is inert.
 */
import React from "react";
import * as Sentry from "@sentry/react";

function DefaultFallback({ error, resetError }) {
  return (
    <div
      data-testid="app-error-boundary-fallback"
      className="flex min-h-[50vh] items-center justify-center p-6"
    >
      <div className="max-w-md w-full rounded-2xl border border-brand-border bg-white p-6 shadow-sm">
        <h2 className="text-lg font-serif text-brand-ink mb-2">Something went wrong</h2>
        <p className="text-sm text-brand-muted mb-4">
          The page hit an unexpected error. Try again — if it keeps happening,
          our team has been automatically notified.
        </p>
        <details className="text-xs text-brand-muted mb-4">
          <summary className="cursor-pointer">Technical details</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words">{String(error)}</pre>
        </details>
        <button
          data-testid="app-error-boundary-retry"
          onClick={resetError}
          className="w-full rounded-lg bg-brand-navy text-white py-2 px-4 text-sm font-medium"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children, fallback }) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => scope.setTag("app.area", "frontend")}
      fallback={fallback || DefaultFallback}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default AppErrorBoundary;
